// httpsCallable() reliably fails to reach europe-west3 Cloud Functions from this
// app's RN/Hermes runtime (its internal fetchImpl() throws and the SDK discards the
// real error, surfacing only a generic FunctionsError('internal', 'internal')) while
// a plain fetch() to the identical URL and payload succeeds every time — proven via
// repeated back-to-back comparison during the password-reset investigation this was
// built from (raw fetch reached the deployed function and got real, correctly-parsed
// responses, including a legitimate RESOURCE_EXHAUSTED rate-limit rejection, in cases
// where httpsCallable never reached the server at all). This replicates the callable
// wire protocol directly over fetch as a proven-working substitute.
interface CallableResponse<T> {
  result?: T;
  error?: { message?: string; status?: string; details?: unknown };
}

/**
 * Thrown for every failure. `status` is the callable error status (e.g.
 * 'NOT_FOUND', 'UNAVAILABLE'), `reason` the server's `details.reason` when it
 * sends one, and `network` is true when the request never got a response.
 */
export class CallableFetchError extends Error {
  status: string;
  reason: string | null;
  details: unknown;
  network: boolean;

  constructor(message: string, { status = 'UNKNOWN', details = null, network = false }: { status?: string; details?: unknown; network?: boolean } = {}) {
    super(message);
    this.name = 'CallableFetchError';
    this.status = status;
    this.details = details;
    this.network = network;
    const reason = (details as { reason?: unknown } | null)?.reason;
    this.reason = typeof reason === 'string' ? reason : null;
  }
}

export async function callFunctionViaFetch<T = unknown>(
  url: string,
  data: Record<string, unknown>,
  options: { idToken?: string | null } = {},
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Same header the callable SDK sends; the function sees it as request.auth.
  if (options.idToken) headers.Authorization = `Bearer ${options.idToken}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ data }),
    });
  } catch (e) {
    throw new CallableFetchError(e instanceof Error ? e.message : 'Network request failed.', { status: 'UNAVAILABLE', network: true });
  }

  let json: CallableResponse<T> | null = null;
  try {
    json = await response.json();
  } catch {
    // leave json null — handled below
  }

  if (json?.error) {
    throw new CallableFetchError(json.error.message || 'Request failed.', { status: json.error.status, details: json.error.details });
  }
  if (!response.ok || !json || typeof json.result === 'undefined') {
    throw new CallableFetchError(`Request failed with status ${response.status}.`, {
      status: response.status >= 500 ? 'UNAVAILABLE' : 'UNKNOWN',
      network: response.status >= 500,
    });
  }
  return json.result;
}
